import { httpsCallable } from 'firebase/functions'
import { functions } from './firebase'

export interface Letter {
  id: string
  body: string
  createdAt: Date
  status: 'pending' | 'approved' | 'rejected'
  rejectionReason?: string
}

export interface CreateLetterResponse {
  success: boolean
  letterId?: string
  error?: string
  rejectionReason?: string
}

export interface GetRandomLetterResponse {
  success: boolean
  letter?: Letter
  error?: string
  noMoreLetters?: boolean
}

export interface GetSpecialLetterResponse {
  success: boolean
  letter?: Letter
  error?: string
}

// Create a new letter
export const createLetter = async (body: string): Promise<CreateLetterResponse> => {
  const createLetterFn = httpsCallable<{ body: string }, CreateLetterResponse>(
    functions,
    'createLetter'
  )
  
  try {
    const result = await createLetterFn({ body })
    return result.data
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to create letter'
    return {
      success: false,
      error: errorMessage,
    }
  }
}

// Get a random letter from the pool
export const getRandomLetter = async (): Promise<GetRandomLetterResponse> => {
  const getRandomLetterFn = httpsCallable<Record<string, never>, GetRandomLetterResponse>(
    functions,
    'getRandomLetter'
  )
  
  try {
    const result = await getRandomLetterFn({})
    return result.data
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to get letter'
    return {
      success: false,
      error: errorMessage,
    }
  }
}

// Get the special static letter (same for all users)
export const getSpecialLetter = async (): Promise<GetSpecialLetterResponse> => {
  const getSpecialLetterFn = httpsCallable<Record<string, never>, GetSpecialLetterResponse>(
    functions,
    'getSpecialLetter'
  )
  
  try {
    const result = await getSpecialLetterFn({})
    return result.data
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to get special letter'
    return {
      success: false,
      error: errorMessage,
    }
  }
}

// Get user's own letters
export const getMyLetters = async (): Promise<{ success: boolean; letters?: Letter[]; error?: string }> => {
  const getMyLettersFn = httpsCallable<Record<string, never>, { success: boolean; letters?: Letter[]; error?: string }>(
    functions,
    'getMyLetters'
  )
  
  try {
    const result = await getMyLettersFn({})
    return result.data
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to get letters'
    return {
      success: false,
      error: errorMessage,
    }
  }
}
